
import React, { useState, useEffect } from 'react';
import {
   Briefcase, MapPin, Clock, ArrowLeft, CheckCircle, X, ArrowRight,
   Sparkles, Upload, Linkedin, Loader2, Heart, PenTool, CheckCircle2,
   Zap, TrendingUp, PlayCircle, Instagram, Facebook, FileText, ChevronDown, User, ShieldCheck,
   Send, Phone, Mail, Globe, Twitter, Youtube, Plus, Trash2, UserCircle2, MessageSquare, LayoutTemplate, Calendar, Search, SlidersHorizontal, Menu, Building
} from 'lucide-react';
import { geocodeAddress, getDistanceFromLatLonInKm } from '../services/locationService';
import SectionRenderer from './SectionRenderer';
import { MOCK_CLIENTS, MOCK_VACANCIES } from '../constants';
import { analyzeUploadedCV, generateCandidateMotivationV2 } from '../services/geminiService';
import { BrandingConfig, Vacancy, Client, WebsiteSection } from '../types';
import { supabase_submit_application, supabase_get_vacancies } from '../services/supabaseService';
import { CANDIDATE_SOURCE_WEBSITE } from '../utils/candidateSource';
import { triggerAutomation } from '../services/automationService';
import { marked } from 'marked';

interface CareersPageProps {
   vacancyTitle?: string;
   currentVacancy?: any;
   clientId?: string;
   previewClient?: any;
   propClient?: any;
   customBranding?: BrandingConfig;
   isEmbedded?: boolean;
   isCustomDomain?: boolean;
   forcePageId?: string;
   forceVacancyId?: string;
   onBack?: () => void;
   onSectionClick?: (sectionId: string) => void;
   activePageId?: string;
}

const PublicVacancyView: React.FC<CareersPageProps> = ({ onBack, vacancyTitle, currentVacancy, clientId = "c1", customBranding, propClient, previewClient, onSectionClick, activePageId: forcePageId, isEmbedded, isCustomDomain, forceVacancyId }) => {
   // Prioritize passed client data (Public or Preview), then fallback to ID lookup, then Mock
   const client = (propClient || previewClient || MOCK_CLIENTS.find(c => c.id === clientId) || MOCK_CLIENTS[0]) as any;

   // State
   const [view, setView] = useState<'home' | 'detail'>('home');
   const [selectedVacancy, setSelectedVacancy] = useState<any>(null);
   const [showWizard, setShowWizard] = useState(false);
   const [wizardStep, setWizardStep] = useState(1);
   const [file, setFile] = useState<File | null>(null);
   const [realVacancies, setRealVacancies] = useState<Vacancy[]>([]);
   const [isLoadingVacancies, setIsLoadingVacancies] = useState(false);

   // Page Routing State
   const [currentPageSlug, setCurrentPageSlug] = useState<string>('');
   const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

   // Dynamic routing helper
   const getPageHref = (page: any) => {
      if (forcePageId) return '#';
      const base = isCustomDomain ? '' : `/${client?.name?.replace(/\s+/g, '')}`;
      const slugSegment = page.isHome ? '' : (page.slug?.startsWith('/') ? page.slug : `/${page.slug}`);
      return `${base}${slugSegment}` || '/';
   };

   useEffect(() => {
      if (forcePageId) return; // Editor controls view

      const path = window.location.pathname;
      const parts = path.split('/').filter(Boolean);
      let foundSlug = '';
      if (isCustomDomain) {
         foundSlug = parts.length > 0 ? '/' + parts.join('/') : '';
      } else {
         foundSlug = parts.length > 1 ? '/' + parts.slice(1).join('/') : '';
      }
      
      setCurrentPageSlug(foundSlug);
   }, [typeof window !== 'undefined' ? window.location.pathname : null, forcePageId]);

   // Application Data
   const [applicant, setApplicant] = useState({
      firstName: '', lastName: '', email: '', linkedin: '', phone: '', country: '', currentStatus: '',
      cvSummary: '', matchScore: 0, matchReason: '', cultureFit: '', tags: [] as string[],
      motivationText: '',
      motivationType: 'ai',
      strongPoints: [] as string[],
      growthPoints: [] as string[],
      rawMotivation: '',
      newPointInput: '',
      newGrowthInput: '',
      consentPrivacy: false,
      consentTerms: false
   });

   // AI States
   const [isAnalyzing, setIsAnalyzing] = useState(false);
   const [isGeneratingMot, setIsGeneratingMot] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [analysisDone, setAnalysisDone] = useState(false);
   const [parsedData, setParsedData] = useState<any>(null);
   const [showLinkedInModal, setShowLinkedInModal] = useState(false);

   // Fetch Real Vacancies
   useEffect(() => {
      const fetchVacancies = async () => {
         if (clientId) {
            setIsLoadingVacancies(true);
            try {
               const data = await supabase_get_vacancies(clientId);
               setRealVacancies(data || []);
            } catch (e) { console.error("Err fetching vacancies", e); }
            finally { setIsLoadingVacancies(false); }
         }
      };
      fetchVacancies();
   }, [clientId]);

   // Combine real vacancies with current edited vacancy if needed
   const baseVacancies = realVacancies.length > 0 ? realVacancies : MOCK_VACANCIES;

   const allVacancies = React.useMemo(() => {
       const mapped = baseVacancies.map(v =>
          (currentVacancy && v.id === currentVacancy.id) ? currentVacancy : v
       );
       if (currentVacancy && !mapped.find(v => v.id === currentVacancy.id)) {
          mapped.push(currentVacancy);
       }
       return mapped;
   }, [baseVacancies, currentVacancy]);

   const visibleVacancies = React.useMemo(() => {
       return forcePageId
          ? allVacancies.filter(v => realVacancies.length > 0 ? (v.clientId === clientId || (v as any).client_id === clientId) : true) 
          : allVacancies.filter(v =>
             (v.clientId === clientId || (v as any).client_id === clientId) &&
             v.data?.publishedToSite === true
          );
   }, [allVacancies, forcePageId, realVacancies.length, clientId]);

   const [vacancySearch, setVacancySearch] = useState('');
   const [vacancyLocation, setVacancyLocation] = useState('');
   const [vacancyRadius, setVacancyRadius] = useState<number>(25);
   const [locationCoords, setLocationCoords] = useState<{lat: number; lng: number} | null>(null);
   const [isGeocoding, setIsGeocoding] = useState(false);
   const [vacancyCoords, setVacancyCoords] = useState<Record<string, {lat: number; lng: number}>>({});

   // Dynamic Filter States
   const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
   const [selectedEducation, setSelectedEducation] = useState<string[]>([]);
   const [selectedExperience, setSelectedExperience] = useState<string[]>([]);
   const [selectedCommitment, setSelectedCommitment] = useState<string[]>([]);
   const [selectedClients, setSelectedClients] = useState<string[]>([]);

   const uniqueBranches = Array.from(new Set(visibleVacancies.map(v => v.data?.category).filter(Boolean))) as string[];
   const uniqueEducation = Array.from(new Set(visibleVacancies.map(v => v.data?.education).filter(Boolean))) as string[];
   const uniqueExperience = Array.from(new Set(visibleVacancies.map(v => v.data?.experience).filter(Boolean))) as string[];
   const uniqueCommitment = Array.from(new Set(visibleVacancies.map(v => v.data?.commitment).filter(Boolean))) as string[];
   const uniqueClients = Array.from(new Set(visibleVacancies.map(v => v.data?.clientName || v.clientId).filter(Boolean))) as string[];

   // Debounced search location geocoding
   useEffect(() => {
      if (!vacancyLocation.trim()) {
         setLocationCoords(null);
         return;
      }
      setIsGeocoding(true);
      const timer = setTimeout(async () => {
         const coords = await geocodeAddress(vacancyLocation);
         setLocationCoords(coords);
         setIsGeocoding(false);
      }, 800);
      return () => clearTimeout(timer);
   }, [vacancyLocation]);

   // Geocode visible vacancies locations
   useEffect(() => {
      let isMounted = true;
      const resolveLocations = async () => {
          let changed = false;
          const newCoords = { ...vacancyCoords };
          
          for (const v of visibleVacancies) {
              const loc = v.data?.location || client.address_city;
              if (loc && !newCoords[loc] && newCoords[loc] !== null) {
                  const coords = await geocodeAddress(loc);
                  newCoords[loc] = coords || null as any; // cache null to avoid re-fetching
                  changed = true;
              }
          }
          if (changed && isMounted) {
              setVacancyCoords(newCoords);
          }
      };
      
      resolveLocations();
      return () => { isMounted = false; };
   }, [visibleVacancies, client.address_city]);

   const filteredVacancies = visibleVacancies.filter(vac => {
      if (vacancySearch) {
         const term = vacancySearch.toLowerCase();
         if (!vac.title.toLowerCase().includes(term) && !(vac.description || '').toLowerCase().includes(term)) {
            return false;
         }
      }
      if (vacancyLocation && locationCoords) {
         const vacLoc = vac.data?.location || client.address_city;
         const vCoords = vacLoc ? vacancyCoords[vacLoc] : null;
         if (vCoords) {
            const dist = getDistanceFromLatLonInKm(locationCoords.lat, locationCoords.lng, vCoords.lat, vCoords.lng);
            if (dist > vacancyRadius) return false;
         } else {
            return false; // Filter out if we strictly search by radius and no coords exist
         }
      }

      if (selectedBranches.length > 0 && !selectedBranches.includes(vac.data?.category || '')) return false;
      if (selectedEducation.length > 0 && !selectedEducation.includes(vac.data?.education || '')) return false;
      if (selectedExperience.length > 0 && !selectedExperience.includes(vac.data?.experience || '')) return false;
      if (selectedCommitment.length > 0 && !selectedCommitment.includes(vac.data?.commitment || '')) return false;
      if (selectedClients.length > 0 && !selectedClients.includes(vac.data?.clientName || vac.clientId || '')) return false;

      return true;
   });

   useEffect(() => {
      if (vacancyTitle) {
         const isMatch = (v: any, vt: string) => {
             if (v.title === vt) return true;
             const slugified = v.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
             const vtSlugified = vt.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
             return slugified === vtSlugified;
         };

         let vac = allVacancies.find(v => isMatch(v, vacancyTitle) && (v.clientId === clientId || (v as any).client_id === clientId));
         if (!vac && currentVacancy && isMatch(currentVacancy, vacancyTitle)) {
             vac = currentVacancy;
         }

         if (vac) {
            setSelectedVacancy({ ...vac, client });
            setView('detail');
         } else if (!currentVacancy) {
            const fallbackVac = MOCK_VACANCIES.find(v => isMatch(v, vacancyTitle));
            if (fallbackVac) {
               setSelectedVacancy({ ...fallbackVac, client });
               setView('detail');
            } else {
               setSelectedVacancy({
                  id: 'temp', title: vacancyTitle, client,
                  description: 'Dit is een preview van de vacature die je momenteel bewerkt.',
                  responsibilities: {}
               });
               setView('detail');
            }
         }
      }
   }, [vacancyTitle, currentVacancy, clientId, allVacancies]);



   const handleAddPoint = (type: 'strong' | 'growth') => {
      if (type === 'strong' && applicant.newPointInput.trim()) {
         setApplicant(prev => ({ ...prev, strongPoints: [...prev.strongPoints, prev.newPointInput], newPointInput: '' }));
      } else if (type === 'growth' && applicant.newGrowthInput.trim()) {
         setApplicant(prev => ({ ...prev, growthPoints: [...prev.growthPoints, prev.newGrowthInput], newGrowthInput: '' }));
      }
   };

   const handleRemovePoint = (type: 'strong' | 'growth', index: number) => {
      if (type === 'strong') {
         setApplicant(prev => ({ ...prev, strongPoints: prev.strongPoints.filter((_, i) => i !== index) }));
      } else {
         setApplicant(prev => ({ ...prev, growthPoints: prev.growthPoints.filter((_, i) => i !== index) }));
      }
   };

   const handleGenerateMotivation = async () => {
      setIsGeneratingMot(true);
      try {
         const res = await generateCandidateMotivationV2(
            selectedVacancy.title,
            client.name,
            applicant.strongPoints,
            applicant.growthPoints,
            applicant.rawMotivation || 'Ik ben enthousiast over deze rol.'
         );
         setApplicant(prev => ({ ...prev, motivationText: res }));
      } catch (e) { console.error(e); } finally { setIsGeneratingMot(false); }
   };

   const handleSubmitApplication = async () => {
      setIsSubmitting(true);
      try {
         const newCandidateId = await supabase_submit_application(selectedVacancy.id, client.id, {
            firstName: applicant.firstName,
            lastName: applicant.lastName,
            email: applicant.email,
            phone: applicant.phone,
            linkedin: applicant.linkedin,
            country: applicant.country,
            currentStatus: applicant.currentStatus,
            motivation: applicant.motivationText,
            cvSummary: applicant.cvSummary,
            matchScore: applicant.matchScore,
            matchReason: applicant.matchReason,
            cultureFit: applicant.cultureFit,
            file: file, // Pass the file object
            parsedData: parsedData, // Pass full AI analysis
            source: CANDIDATE_SOURCE_WEBSITE,
         });

         // Trigger automations for the new candidate
         if (newCandidateId) {
            await triggerAutomation('application_created', {
               vacancyId: selectedVacancy.id,
               candidateId: newCandidateId,
            });
         }

         setWizardStep(3);
      } catch (error: any) { alert("Error: " + error.message); } finally { setIsSubmitting(false); }
   };

   const handleSubmitLinkedIn = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!applicant.firstName || !applicant.lastName || !applicant.email || !applicant.linkedin) {
         alert("Vul alsjeblieft je naam, e-mail en LinkedIn URL in.");
         return;
      }
      setIsSubmitting(true);
      try {
         const newCandidateId = await supabase_submit_application(selectedVacancy.id, client.id, {
            ...applicant,
            motivation: "Kandidaat heeft interesse getoond via de 'Blijf op de hoogte' LinkedIn connectie flow.",
            cvSummary: "Automatisch gegenereerd profiel via LinkedIn connectie (Open Sollicitatie).",
            matchScore: 0,
            matchReason: "Open sollicitatie / Connectie per LinkedIn.",
            cultureFit: "",
            file: null,
            parsedData: null,
            source: CANDIDATE_SOURCE_WEBSITE,
         });

         if (newCandidateId) {
            await triggerAutomation('application_created', {
               vacancyId: selectedVacancy.id,
               candidateId: newCandidateId,
            });
         }
         
         setShowLinkedInModal(false);
         setWizardStep(3);
         setShowWizard(true);
      } catch (error: any) { alert("Error: " + error.message); } finally { setIsSubmitting(false); }
   };

   const scrollToSection = (id: string) => {
      if (view !== 'home') {
         setView('home');
         setTimeout(() => {
            const element = document.getElementById(id);
            if (element) element.scrollIntoView({ behavior: 'smooth' });
         }, 100);
      } else {
         const element = document.getElementById(id);
         if (element) element.scrollIntoView({ behavior: 'smooth' });
      }
   };

   const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
         const f = e.target.files[0];
         setFile(f);
         setIsAnalyzing(false);
         setAnalysisDone(true);
         setParsedData(null);
         console.log("File selected. Skipping AI Analysis to save costs.");
      }
   };

   // --- RENDERERS ---

   const getSectionStyle = (section: WebsiteSection, baseClass: string) => {
      if (onSectionClick) {
         return `${baseClass} cursor-pointer hover:ring-2 hover:ring-indigo-500 hover:ring-offset-2 transition-all relative group shadow-sm hover:shadow-xl hover:z-50`;
      }
      return baseClass;
   };

   const renderTeam = (section: WebsiteSection) => (
      <section
         key={section.id}
         id={section.id}
         onClick={(e) => {
            if (onSectionClick) {
               e.stopPropagation();
               onSectionClick(section.id);
            }
         }}
         className={getSectionStyle(section, `py-24 ${section.styles?.padding || ''}`)}
         style={{ backgroundColor: section.styles?.backgroundColor || '#ffffff', color: section.styles?.textColor || '#0f172a', backgroundImage: section.styles?.backgroundImage ? `url(${section.styles?.backgroundImage})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', fontFamily: section.styles?.fontFamily || 'inherit' }}
      >
         <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
               <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-6">{section.title}</h2>
               {section.subtitle && <p className="text-lg opacity-80">{section.subtitle}</p>}
            </div>
            {section.content?.teamMembers && section.content.teamMembers.length > 0 ? (
               <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                  {section.content.teamMembers.map((member: any) => (
                     <div key={member.id} className="group relative">
                        <div className="aspect-[4/5] rounded-2xl overflow-hidden mb-4 bg-slate-100 relative border border-slate-200 shadow-sm">
                           {member.imageUrl ? (
                              <img src={member.imageUrl} alt={member.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                           ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-300">
                                 <UserCircle2 size={64} />
                              </div>
                           )}
                           {member.linkedin && (
                              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0 text-indigo-600 shadow-lg cursor-pointer">
                                 <a href={member.linkedin} target="_blank" rel="noopener noreferrer"><Linkedin size={18} /></a>
                              </div>
                           )}
                        </div>
                        <h3 className="font-bold text-lg mb-1">{member.name}</h3>
                        <p className="text-sm font-medium opacity-60 mb-3 text-indigo-600">{member.role}</p>
                        {member.description && <p className="text-xs opacity-80 leading-relaxed">{member.description}</p>}
                     </div>
                  ))}
               </div>
            ) : (
               <div className="text-center opacity-50 italic">Geen teamleden toegevoegd. Voeg teamleden toe via de editor.</div>
            )}
         </div>
      </section>
   );

   const renderPlaceholder = (section: WebsiteSection, icon: React.ReactNode) => (
      <section
         key={section.id}
         id={section.id}
         onClick={(e) => {
            if (onSectionClick) {
               e.stopPropagation();
               onSectionClick(section.id);
            }
         }}
         className={getSectionStyle(section, `py-24 ${section.styles?.padding || ''} text-center`)}
         style={{ backgroundColor: section.styles?.backgroundColor || '#f8fafc', color: section.styles?.textColor || '#0f172a' }}
      >
         <div className="max-w-2xl mx-auto px-6">
            <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-6">
               {icon}
            </div>
            <h2 className="text-2xl font-black mb-4">{section.title}</h2>
            {section.subtitle && <p className="text-lg opacity-80 mb-6">{section.subtitle}</p>}
            <p className="text-sm opacity-60 p-4 border border-dashed border-slate-300 rounded-lg">
               Sectie-type: <b>{section.type}</b> is in de editor toegevoegd, maar heeft nog geen specifieke live weergave hier.
            </p>
         </div>
      </section>
   );

   const renderGallery = (section: WebsiteSection) => renderPlaceholder(section, <LayoutTemplate size={24} />);
   const renderFaq = (section: WebsiteSection) => renderPlaceholder(section, <MessageSquare size={24} />);
   const renderContact = (section: WebsiteSection) => renderPlaceholder(section, <Phone size={24} />);

   const renderHero = (section: WebsiteSection) => (
      <section
         key={section.id}
         id={section.id}
         onClick={(e) => {
            if (onSectionClick) {
               e.stopPropagation();
               onSectionClick(section.id);
            }
         }}
         className={getSectionStyle(section, "relative min-h-[600px] flex items-center overflow-hidden")}
         style={{ backgroundColor: section.styles?.backgroundColor || '#111827' }}
      >
         {onSectionClick && (
            <div className="absolute top-2 right-2 bg-indigo-600 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-50">
               Edit Hero
            </div>
         )}
         <div className="absolute inset-0 z-0">
            {section.content?.imageUrl && (
               <>
                  <div className="absolute inset-0 z-10 opacity-70" style={{ background: `linear - gradient(to right, ${section.styles?.backgroundColor || '#111827'} 0 %, ${customBranding?.primaryColor || '#111827'} 99 100 %)` }}></div>
                  <img src={section.content.imageUrl} className="w-full h-full object-cover" />
               </>
            )}
         </div>
         <div className="max-w-7xl mx-auto px-6 relative z-20 w-full flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="max-w-3xl space-y-8 pt-12 md:pt-0">
               {client?.logo_url && (
                  <img src={client.logo_url} alt={client?.name || 'Company Logo'} className="h-16 object-contain mb-2 bg-white/90 p-3 rounded-xl shadow-lg" />
               )}
               <h1 className="text-5xl md:text-7xl font-black leading-[0.9] tracking-tight uppercase drop-shadow-md [&>p]:inline" style={{ color: section.styles?.textColor || '#FFFFFF' }}
                   dangerouslySetInnerHTML={{ __html: section.title || '' }} 
               />
               <div 
                  className="font-medium text-lg max-w-lg leading-relaxed drop-shadow-sm [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-5 [&>h1]:text-3xl [&>h1]:font-bold [&>h2]:text-2xl [&>h2]:font-bold" 
                  style={{ color: section.styles?.textColor ? `${section.styles.textColor}CC` : '#FFFFFFE6' }}
                  dangerouslySetInnerHTML={{ __html: section.subtitle || '' }}
               />
               <div className="flex flex-wrap gap-4 pt-4">
                  <button onClick={() => scrollToSection('vacancies')} className="text-white px-8 py-4 rounded-sm font-black text-sm uppercase tracking-widest transition-transform hover:-translate-y-1 shadow-lg" style={{ backgroundColor: section.styles?.buttonColor || customBranding?.primaryColor || '#FF6B00', color: section.styles?.buttonTextColor || '#ffffff' }}>
                     Bekijk Vacatures
                  </button>
               </div>
            </div>
         </div>
      </section>
   );

   const renderTextSection = (section: WebsiteSection) => (
      <section
         key={section.id}
         id={section.id}
         onClick={(e) => {
            if (onSectionClick) {
               e.stopPropagation();
               onSectionClick(section.id);
            }
         }}
         className={getSectionStyle(section, "py-24")}
         style={{ backgroundColor: section.styles?.backgroundColor || '#FFFFFF' }}
      >
         {onSectionClick && (
            <div className="absolute top-2 right-2 bg-indigo-600 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-50">
               Edit Text
            </div>
         )}
         <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
               <h2 className="text-4xl font-black uppercase tracking-tight [&>p]:inline" style={{ color: customBranding?.secondaryColor || '#111827' }} dangerouslySetInnerHTML={{ __html: section.title || '' }} />
               <div 
                  className="text-lg font-medium leading-relaxed whitespace-pre-wrap [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-5 [&>h1]:text-3xl [&>h1]:font-bold [&>h2]:text-2xl [&>h2]:font-bold" 
                  style={{ color: section.styles?.textColor || customBranding?.fontColor || '#475569' }}
                  dangerouslySetInnerHTML={{ __html: section.content?.text || '' }}
               />
            </div>
            {section.content?.imageUrl && (
               <div className="relative group">
                  <div className="absolute top-4 left-4 w-full h-full border-2 rounded-2xl z-0 transition-transform group-hover:translate-x-2 group-hover:translate-y-2" style={{ borderColor: customBranding?.primaryColor || '#000' }}></div>
                  <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl aspect-video bg-slate-100">
                     <img src={section.content.imageUrl} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
               </div>
            )}
         </div>
      </section>
   );

   const renderFeatures = (section: WebsiteSection) => (
      <section
         key={section.id}
         id={section.id}
         onClick={(e) => {
            if (onSectionClick) {
               e.stopPropagation();
               onSectionClick(section.id);
            }
         }}
         className={getSectionStyle(section, "py-24")}
         style={{ backgroundColor: section.styles?.backgroundColor || '#F8FAFC' }}
      >
         {onSectionClick && (
            <div className="absolute top-2 right-2 bg-indigo-600 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-50">
               Edit Features
            </div>
         )}
         <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
               <h2 className="text-3xl font-black uppercase tracking-tight mb-4 [&>p]:inline" style={{ color: section.styles?.textColor || '#111827' }} dangerouslySetInnerHTML={{ __html: section.title || '' }} />
               <p className="max-w-2xl mx-auto text-slate-500" dangerouslySetInnerHTML={{ __html: section.subtitle || '' }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
               {section.content?.items?.map((item: any, i: number) => (
                  <div key={i} className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-all">
                     <h3 className="font-bold text-lg mb-2" style={{ color: customBranding?.primaryColor }}>{item.title}</h3>
                     <p className="text-slate-600 text-sm">{item.description}</p>
                  </div>
               ))}
            </div>
         </div>
      </section>
   );


   const renderFilterBlock = (title: string, options: string[], selected: string[], setter: (val: string[]) => void) => {
      if (options.length === 0) return null;
      return (
         <div className="p-5 border-b border-slate-100 last:border-b-0">
            <h4 className="text-xs font-bold text-slate-800 mb-3 uppercase tracking-wider">{title}</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-2">
               {options.map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer group">
                     <input 
                        type="checkbox" 
                        checked={selected.includes(opt)}
                        onChange={(e) => {
                           if (e.target.checked) setter([...selected, opt]);
                           else setter(selected.filter(x => x !== opt));
                        }}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shadow-sm transition-colors" 
                        style={{ accentColor: customBranding?.primaryColor || '#e51b23' }}
                     />
                     <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors break-all line-clamp-1 truncate" title={opt}>{opt}</span>
                  </label>
               ))}
            </div>
         </div>
      );
   };

   const renderVacancies = (section: WebsiteSection) => {
      const sectionFilteredVacancies = filteredVacancies.filter(vac => {
         if (!section.content?.clientFilters || section.content.clientFilters.length === 0) return true;
         return section.content.clientFilters.includes(vac.clientId || (vac as any).client_id) || section.content.clientFilters.includes(vac.data?.clientName);
      });

      return (
      <section
         key={section.id}
         id={section.id || "vacancies"}
         onClick={(e) => {
            if (onSectionClick) {
               e.stopPropagation();
               onSectionClick(section.id);
            }
         }}
         className={`relative border-2 border-transparent transition-all z-20 overflow-visible ${onSectionClick ? 'cursor-pointer hover:border-indigo-400 group' : ''}`}
         style={{ backgroundColor: section.styles?.backgroundColor || '#F8FAFC' }}
      >
         {onSectionClick && (
             <div className="absolute top-2 right-2 bg-indigo-600 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-[100]">
               Edit Vacancies
            </div>
         )}
         
         <div className={`py-6 sm:py-16 md:py-24 ${section.styles?.padding === 'small' ? 'py-8' : section.styles?.padding === 'large' ? 'py-32' : 'py-24'}`}>
            <div className="max-w-7xl mx-auto px-6 mb-12">
               <h2 className={`text-4xl md:text-5xl font-black uppercase tracking-tight mb-4 [&>p]:inline ${section.styles?.textShadow || 'drop-shadow-sm'}`} style={{ color: section.styles?.textColor || customBranding?.fontColor || customBranding?.primaryColor || '#000' }} dangerouslySetInnerHTML={{ __html: section.title || "Vind Jouw Baan" }} />
               <p className={`font-medium max-w-2xl leading-relaxed ${section.styles?.subtitleStyles?.fontFamily || ''} ${section.styles?.subtitleStyles?.fontSize || 'text-lg'}`} style={{ color: section.styles?.subtitleStyles?.color || section.styles?.textColor || customBranding?.fontColor || '#64748b' }} dangerouslySetInnerHTML={{ __html: section.subtitle || "Gebruik de filters om de vacature te vinden die perfect bij jou past." }} />
            </div>

         <div className="max-w-7xl mx-auto px-6 relative z-10">
            
            {/* SEARCH STRIP */}
            <div className="bg-white border border-slate-200 shadow-sm flex flex-col md:flex-row mb-12 w-full rounded-sm overflow-hidden text-slate-900">
               <div className="flex-1 p-3 md:p-4 border-b md:border-b-0 md:border-r border-slate-200 flex flex-col justify-center">
                  <label className="text-[10px] md:text-xs font-bold flex items-center gap-2 mb-1" style={{ color: customBranding?.primaryColor || '#0f172a' }}><Search size={14}/> {section.content?.searchLabel || "Zoekterm"}</label>
                  <input type="text" placeholder={section.content?.searchPlaceholder || "Wat voor werk zoek je?"} value={vacancySearch} onChange={e => setVacancySearch(e.target.value)} className="w-full text-sm outline-none bg-transparent placeholder:text-slate-400 font-medium"/>
               </div>
               <div className="flex-1 p-3 md:p-4 border-b md:border-b-0 md:border-r border-slate-200 flex flex-col justify-center">
                  <label className="text-[10px] md:text-xs font-bold flex items-center gap-2 mb-1" style={{ color: customBranding?.primaryColor || '#0f172a' }}><MapPin size={14}/> {section.content?.locationLabel || "Locatie"}</label>
                  <div className="flex items-center gap-2">
                     <input type="text" placeholder={section.content?.locationPlaceholder || "Waar wil je werken?"} value={vacancyLocation} onChange={e => setVacancyLocation(e.target.value)} className="w-full text-sm outline-none bg-transparent placeholder:text-slate-400 font-medium"/>
                     {isGeocoding && <Loader2 size={14} className="animate-spin text-slate-400"/>}
                  </div>
               </div>
               <div className="w-full md:w-48 p-3 md:p-4 border-b md:border-b-0 md:border-r border-slate-200 flex flex-col justify-center relative">
                  <label className="text-[10px] md:text-xs font-bold flex items-center gap-2 mb-1" style={{ color: customBranding?.primaryColor || '#0f172a' }}>{section.content?.distanceLabel || "Afstand"}</label>
                  <select value={vacancyRadius} onChange={e => setVacancyRadius(Number(e.target.value))} className="w-full text-sm outline-none bg-transparent appearance-none cursor-pointer font-medium text-slate-600">
                     <option value="5">5 km</option>
                     <option value="10">10 km</option>
                     <option value="25">25 km</option>
                     <option value="50">50 km</option>
                     <option value="100">100 km</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-4 bottom-4 text-slate-500 pointer-events-none"/>
               </div>
               <div className="p-3 md:p-4 flex items-center justify-center bg-slate-50 md:bg-white w-full md:w-auto">
                  <button onClick={() => {}} className="w-full md:w-auto px-8 py-3 rounded-sm font-bold flex items-center justify-center gap-2 transition-colors hover:opacity-90 shadow-sm" style={{ backgroundColor: section.styles?.buttonColor || customBranding?.primaryColor || '#e51b23', color: section.styles?.buttonTextColor || '#fff' }}>
                     {section.content?.searchButtonText || "Zoeken"} <ArrowRight size={16}/>
                  </button>
               </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
               {/* SIDEBAR FILTERS (Accordion Style) */}
               <div className="lg:col-span-1 space-y-4">
                  <div className="bg-white rounded-sm border border-slate-200 shadow-sm overflow-hidden lg:sticky lg:top-24">
                     <div className="p-5 border-b border-slate-200">
                        <h3 className="text-xl font-bold" style={{ color: customBranding?.primaryColor || '#0f172a' }}>{section.content?.filtersLabel || "Filters"}</h3>
                     </div>
                     <div className="divide-y divide-slate-100">
                        {renderFilterBlock("Opdrachtgever", uniqueClients, selectedClients, setSelectedClients)}
                        {renderFilterBlock("Branche", uniqueBranches, selectedBranches, setSelectedBranches)}
                        {renderFilterBlock("Dienstverband", uniqueCommitment, selectedCommitment, setSelectedCommitment)}
                        {renderFilterBlock("Opleidingsniveau", uniqueEducation, selectedEducation, setSelectedEducation)}
                        {renderFilterBlock("Ervaring", uniqueExperience, selectedExperience, setSelectedExperience)}
                     </div>
                  </div>
               </div>

               {/* MAIN LIST */}
               <div className="lg:col-span-3">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                     <h2 className="text-[22px] font-bold text-slate-900">{section.content?.resultsText || "Er worden"} {sectionFilteredVacancies.length} {section.content?.resultsLabel || "zoekresultaten weergegeven"}</h2>
                     <div className="flex flex-col items-end gap-1 text-sm">
                        <span className="text-slate-900 font-medium text-[11px] uppercase tracking-widest pl-1">{section.content?.sortLabel || "Sorteer met meest"}</span>
                        <div className="relative">
                           <select className="appearance-none border border-slate-300 rounded-sm py-2 pl-3 pr-10 bg-white text-slate-700 font-medium outline-none hover:border-slate-400 cursor-pointer min-w-[160px] text-sm">
                              <option>{section.content?.sortRelevance || "Relevantie"}</option>
                              <option>{section.content?.sortDate || "Datum"}</option>
                           </select>
                           <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"/>
                        </div>
                     </div>
                  </div>

                  <div className="space-y-4">
                     {isLoadingVacancies ? (
                        <div className="py-20 text-center flex flex-col items-center gap-4 text-slate-400 bg-white rounded-sm border border-slate-200 shadow-sm">
                           <Loader2 className="animate-spin text-slate-300" size={32} />
                           <span className="text-xs font-bold uppercase tracking-widest">Vacatures laden...</span>
                        </div>
                     ) : sectionFilteredVacancies.length > 0 ? (
                        sectionFilteredVacancies.map((vac) => (
                           <div
                              key={vac.id}
                              onClick={() => { setSelectedVacancy({ ...vac, client }); setView('detail'); }}
                              className="bg-white p-6 md:p-7 rounded-sm shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-slate-200 flex flex-col"
                           >
                              <div className="flex justify-between items-start mb-4 gap-4">
                                 <h3 className="text-[20px] font-bold text-slate-900">{vac.title}</h3>
                                 {(() => {
                                    const displayedClient = vac.eindklant || vac.client || vac.clients;
                                    const opdrachtgeverName = vac.data?.clientName || displayedClient?.name || vac.clientId;
                                    const logoUrl = vac.data?.clientLogo || displayedClient?.logo_url || ((MOCK_CLIENTS.find((c: any) => c.id === vac.clientId) as any)?.logo_url);
                                    if (logoUrl) {
                                       return <img src={logoUrl} alt={opdrachtgeverName || 'Opdrachtgever logo'} className="h-10 w-auto max-w-[100px] object-contain shrink-0 bg-white rounded-sm border border-slate-100 p-1 shadow-sm" title={opdrachtgeverName} />;
                                    } else if (opdrachtgeverName && opdrachtgeverName !== clientId && opdrachtgeverName !== client.name && opdrachtgeverName !== 'c1') {
                                       return <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-1 rounded-sm border border-slate-200 shrink-0">{opdrachtgeverName}</span>;
                                    }
                                    return null;
                                 })()}
                              </div>
                              
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 mb-6 text-[13px] text-slate-600 font-medium">
                                 <div className="flex items-center gap-2">
                                    <MapPin size={16} className="text-slate-400" />
                                    <span>{vac.data?.location || client.address_city || 'Netherlands'}</span>
                                 </div>
                                 <div className="flex items-center gap-2">
                                    <Briefcase size={16} className="text-slate-400" />
                                    <span>{typeof vac.data?.salary === 'object' && (vac.data.salary.min > 0 || vac.data.salary.max > 0) ? `€ ${vac.data.salary.min || 0} - ${vac.data.salary.max || 0} per ${vac.data.salary.period === 'Yearly' ? 'jaar' : vac.data.salary.period === 'Hourly' ? 'uur' : 'maand'}` : (vac.data?.salary && typeof vac.data.salary === 'string' ? vac.data.salary : 'Salaris n.t.b.')}</span>
                                 </div>
                                 <div className="flex items-center gap-2">
                                    <Building size={16} className="text-slate-400 shrink-0" />
                                    <span className="truncate">{vac.data?.clientName || vac.eindklant?.name || (vac.clientId && vac.clientId.length < 10 ? vac.clientId : client.name)}</span>
                                 </div>
                                 <div className="flex items-center gap-2">
                                    <Calendar size={16} className="text-slate-400 shrink-0" />
                                    <span>{(vac.deadline || vac.data?.deadline) ? new Date(vac.deadline || vac.data?.deadline).toLocaleDateString('nl-NL') : 'Geen Deadline'}</span>
                                 </div>
                                 <div className="flex items-center gap-2">
                                    <User size={16} className="text-slate-400" />
                                    <span>{vac.data?.education || 'HBO / WO'}</span>
                                 </div>
                                 <div className="flex items-center gap-2">
                                    <Briefcase size={16} className="text-slate-400" />
                                    <span>{vac.data?.experience || 'Vanaf 3 jaar'}</span>
                                 </div>
                              </div>
                              
                              <p className="text-[13px] text-slate-600 leading-relaxed max-w-4xl font-medium">
                                 {((vac.description || vac.data?.finalVacancyContent || '').replace(/<[^>]*>?/gm, '').substring(0, 220) || `Als ${vac.title} ben je verantwoordelijk voor een divers scala aan taken. Je overziet het end-to-end proces en zorgt dat alle stakeholders aangesloten blijven in een dynamische en innovatieve werkomgeving...`)}...
                              </p>
                           </div>
                        ))
                     ) : (
                        <div className="py-24 text-center bg-white rounded-sm border border-slate-200 shadow-sm">
                           <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                              <Search size={24} />
                           </div>
                           <h3 className="font-bold text-slate-900 text-lg mb-1">Geen resultaten gevonden</h3>
                           <p className="text-slate-500 text-sm max-w-sm mx-auto">Pas je zoekopdracht aan of vergroot de zoekstraal om meer vacatures te zien.</p>
                           <button onClick={() => { setVacancySearch(''); setVacancyLocation(''); setVacancyRadius(25); }} className="mt-6 px-6 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm text-xs font-bold uppercase tracking-widest transition-colors">Filters Wissen</button>
                        </div>
                     )}
                  </div>
               </div>
            </div>
          </div>
       </div>
    </section>
   );
   };

   const StepIndicator = () => (
      <div className="flex items-center justify-between mb-8 px-2 relative">
         {[1, 2, 3, 4].map(step => (
            <div key={step} className="flex flex-col items-center relative z-10">
               <div className={`w - 8 h - 8 rounded - full flex items - center justify - center text - xs font - bold transition - all duration - 500 ${step <= wizardStep ? 'text-white scale-110' : 'bg-slate-100 text-slate-400'} `} style={{ backgroundColor: step <= wizardStep ? (customBranding?.primaryColor || '#000') : undefined }}>
                  {step < wizardStep ? <CheckCircle size={16} /> : step}
               </div>
               <span className={`text - [9px] font - bold uppercase tracking - widest mt - 2 ${step <= wizardStep ? 'text-slate-800' : 'text-slate-300'} `}>
                  {['Upload', 'Analyse', 'Motivatie', 'Klaar'][step - 1]}
               </span>
            </div>
         ))}
         <div className="absolute top-4 left-0 w-full h-0.5 bg-slate-100 -z-0"></div>
         <div className="absolute top-4 left-0 h-0.5 transition-all duration-500 -z-0" style={{ width: `${((wizardStep - 1) / 3) * 100}% `, backgroundColor: customBranding?.primaryColor || '#000' }}></div>
      </div>
   );

   const Header = () => (
      <header
         className="sticky top-0 z-40 shadow-sm transition-colors duration-300"
         style={{ backgroundColor: customBranding?.headerColor || '#0F172A', color: customBranding?.headerTextColor || '#FFFFFF', fontFamily: customBranding?.headerFont || customBranding?.font || 'inherit' }}
      >
         <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setView('home')}>
               {customBranding?.logoUrl ? <img src={customBranding.logoUrl} className="w-auto object-contain transition-all" style={{ height: `calc(80px - ${Math.max(0, customBranding.logoPadding ?? 20)}px)` }} alt="Logo" /> : <span className="font-bold text-xl">{client.name}</span>}
            </div>
            <div className="flex items-center gap-4 md:gap-8">
               <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden p-2 rounded-md hover:bg-white/10 transition-colors" aria-label="Open Menu">
                   <Menu size={24} />
               </button>

               <nav className={`hidden md:flex items-center gap-6 font-bold uppercase tracking-wide opacity-90 ${customBranding?.headerFontSize || 'text-xs'}`}>
                  {customBranding?.pages?.length > 0 && (
                     customBranding.pages.map((page: any) => (
                        !page.isHidden && page.slug !== '/vacatures' && page.slug !== 'vacatures' && (
                           <a
                              key={page.id}
                              href={getPageHref(page)}
                              onClick={(e) => {
                                 if (forcePageId) {
                                    e.preventDefault();
                                    // In Editor, we can't easily switch the Editor's activeTab without a callback
                                    // But at least we show the link exists.
                                    alert("Navigation in editor preview is disabled. Use the sidebar to switch pages.");
                                 }
                              }}
                              className={`hover:opacity-100 transition-opacity ${currentPageSlug === page.slug || (page.isHome && !currentPageSlug) ? 'text-red-500' : ''}`}
                           >
                              {page.title}
                           </a>
                        )
                     ))
                  )}
               </nav>
               <button onClick={() => scrollToSection('vacancies')} className="relative px-5 py-2 rounded-sm text-xs font-black uppercase tracking-widest hover:brightness-110 transition-all transform hover:-translate-y-0.5 shadow-lg text-white" style={{ backgroundColor: customBranding?.primaryColor || '#e51b23' }}>
                  Vacatures
                  <span className="absolute -top-2 -right-2 bg-white text-slate-900 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-slate-900">{visibleVacancies.length}</span>
               </button>
            </div>
         </div>

         {/* MOBILE MENU OVERLAY */}
         {isMobileMenuOpen && (
            <div className="fixed inset-0 z-50 bg-slate-900/95 text-white flex flex-col pt-6 px-6 pb-12 animate-in slide-in-from-right duration-300">
               <div className="flex justify-end mb-8">
                  <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                     <X size={24} />
                  </button>
               </div>
               <div className="flex flex-col items-center justify-center flex-grow">
                   <nav className="flex flex-col gap-8 text-xl font-black uppercase tracking-widest text-center">
                      {customBranding?.pages?.length > 0 && (
                         customBranding.pages.map((page: any) => (
                            !page.isHidden && page.slug !== '/vacatures' && page.slug !== 'vacatures' && (
                               <a
                                  key={page.id}
                                  href={getPageHref(page)}
                                  onClick={(e) => {
                                     if (forcePageId) { e.preventDefault(); alert("Navigation in editor preview is disabled."); }
                                     setIsMobileMenuOpen(false);
                                  }}
                                  className={`hover:text-red-500 transition-colors ${currentPageSlug === page.slug || (page.isHome && !currentPageSlug) ? 'text-red-500' : ''}`}
                               >
                                  {page.title}
                               </a>
                            )
                         ))
                      )}
                      
                      {/* Duplicate the CTA in the mobile menu for easy access */}
                      <button onClick={() => { scrollToSection('vacancies'); setIsMobileMenuOpen(false); }} className="mt-8 relative px-6 py-3 rounded-sm text-sm font-black uppercase tracking-widest text-white shadow-lg mx-auto" style={{ backgroundColor: customBranding?.primaryColor || '#e51b23' }}>
                         Vacatures
                         <span className="absolute -top-2 -right-2 bg-white text-slate-900 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 border-slate-900">{visibleVacancies.length}</span>
                      </button>
                   </nav>
               </div>
            </div>
         )}
      </header>
   );

   const Footer = () => (
      <footer className={`py-12 border-t border-slate-800 ${customBranding?.footerFontSize || 'text-xs'}`} style={{ backgroundColor: customBranding?.footerColor || '#000000', color: customBranding?.footerTextColor || '#FFFFFF', fontFamily: customBranding?.footerFont || customBranding?.font || 'inherit' }}>
         <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
               <h4 className="font-black uppercase tracking-widest text-sm mb-4">{client.name}</h4>
               <p className="text-xs opacity-60 leading-relaxed whitespace-pre-wrap">
                  {customBranding?.footerConfig?.copyrightText || 'Wij bouwen aan de toekomst met getalenteerde mensen. Bekijk onze vacatures en groei met ons mee.'}
               </p>
            </div>
            <div>
               <h4 className="font-bold uppercase tracking-widest text-[10px] mb-6 opacity-50">{customBranding?.footerConfig?.navTitle || 'Navigatie'}</h4>
               <ul className="space-y-2 text-xs font-medium opacity-80">
                  <li><button onClick={() => scrollToSection('about')} className="hover:opacity-100 opacity-70 transition-opacity">Over ons</button></li>
                  <li><button onClick={() => scrollToSection('vacancies')} className="hover:opacity-100 opacity-70 transition-opacity">Vacatures</button></li>
                  <li><button onClick={() => scrollToSection('contact')} className="hover:opacity-100 opacity-70 transition-opacity">Contact</button></li>
               </ul>
            </div>
            <div>
               <h4 className="font-bold uppercase tracking-widest text-[10px] mb-6 opacity-50">{customBranding?.footerConfig?.contactTitle || 'Contact'}</h4>
               <ul className="space-y-2 text-xs font-medium opacity-80">
                  {client.address_city && <li>{client.address_city}</li>}
                  {client.email && <li>{client.email}</li>}
                  {client.phone && <li>{client.phone}</li>}
               </ul>
            </div>
            <div>
               <h4 className="font-bold uppercase tracking-widest text-[10px] mb-6 opacity-50">{customBranding?.footerConfig?.socialTitle || 'Volg ons'}</h4>
               <div className="flex gap-4">
                  {customBranding?.socialLinks?.map((social: any) => (
                     <a key={social.platform} href={social.url} target="_blank" rel="noopener noreferrer" className="opacity-60 hover:opacity-100 transition-opacity">
                        {social.platform === 'linkedin' && <Linkedin size={18} />}
                        {social.platform === 'instagram' && <Instagram size={18} />}
                        {social.platform === 'facebook' && <Facebook size={18} />}
                        {social.platform === 'twitter' && <Twitter size={18} />}
                     </a>
                  ))}
               </div>
            </div>
         </div>
         <div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-white/10 flex justify-between items-center text-[10px] opacity-40 font-bold uppercase tracking-widest">
            <p>&copy; {new Date().getFullYear()} {client.name}. All rights reserved.</p>
            <p>Powered by Werftools</p>
         </div>
      </footer>
   );

   return (
      <div className="min-h-screen font-sans bg-white selection:bg-orange-100 selection:text-orange-900 flex flex-col"
         style={{
            fontFamily: customBranding?.font || 'Roboto, sans-serif',
            color: customBranding?.fontColor || '#334155'
         }}>

         {onBack && !isEmbedded && (
            <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center justify-between sticky top-0 z-[60] shadow-sm text-slate-900">
               <button onClick={onBack} className="flex items-center gap-2 text-[10px] font-bold text-slate-500 hover:text-slate-900 transition-colors uppercase tracking-widest">
                  <ArrowLeft size={14} /> Back to Editor
               </button>
               <span className="text-[10px] bg-slate-100 text-slate-600 px-3 py-1 rounded-full font-bold uppercase tracking-widest border border-slate-200">Preview Mode</span>
            </div>
         )}

         {!isEmbedded && <Header />}

         <div className="flex-grow">
            {view === 'home' ? (
               <main className="animate-in fade-in duration-500">
                  {/* DYNAMIC SECTIONS RENDERER */}
                  {/* DYNAMIC SECTIONS RENDERER */}
                  {(() => {
                     // 1. Determine Sections to Render
                     let sectionsToRender: WebsiteSection[] = [];

                     if (customBranding?.pages?.length > 0) {
                        // MULTI-PAGE MODE
                        let activePage;
                        if (forcePageId) {
                           activePage = customBranding.pages.find((p: any) => p.id === forcePageId);
                        } else {
                           // Route Matching (with or without leading slash tolerance)
                           activePage = customBranding.pages.find((p: any) => p.slug === currentPageSlug || p.slug === currentPageSlug.replace(/^\//, ''));
                           if (!activePage && (!currentPageSlug || currentPageSlug === '/')) {
                              activePage = customBranding.pages.find((p: any) => p.isHome);
                           }
                        }

                        if (activePage) {
                           sectionsToRender = activePage.sections || [];
                        }
                     } else if (customBranding?.sections) {
                        // LEGACY MODE
                        sectionsToRender = customBranding.sections;
                     }

                     // 2. Render Sections
                     if (sectionsToRender.length > 0) {
                        return sectionsToRender.map((section, idx) => {
                           if (!section.isVisible) return null;

                           // Use Advanced Renderer if elements structure exists
                           if (section.elements && section.elements.length > 0) {
                              return <SectionRenderer key={section.id} section={section} />;
                           }

                           switch (section.type) {
                              case 'hero': return renderHero(section);
                              case 'text_image':
                              case 'about': return renderTextSection(section);
                              case 'features': return renderFeatures(section);
                              case 'vacancies': return renderVacancies(section);
                              case 'team': return renderTeam(section);
                              case 'gallery': return renderGallery(section);
                              case 'faq': return renderFaq(section);
                              case 'contact': return renderContact(section);
                              // Add more renderers here
                              default: return null;
                           }
                        });
                     } else {
                        // FALLBACK TO STATIC LAYOUT IF NO SECTIONS DEFINED
                        return (
                           <>
                              <section className="relative min-h-[600px] flex items-center bg-[#111827] overflow-hidden">
                                 <div className="absolute inset-0 z-0">
                                    <div className="absolute inset-0 z-10 opacity-70" style={{ background: `linear-gradient(to right, #111827 0%, ${customBranding?.primaryColor || '#111827'}99 100%)` }}></div>
                                    {customBranding?.heroImage && <img src={customBranding?.heroImage} className="w-full h-full object-cover" />}
                                 </div>
                                 <div className="max-w-7xl mx-auto px-6 relative z-20 w-full flex flex-col md:flex-row items-center justify-between gap-12">
                                    <div className="max-w-3xl space-y-8">
                                       <h1 className="text-5xl md:text-7xl font-black text-white leading-[0.9] tracking-tight uppercase">
                                          {customBranding?.heroTitle || `Welkom bij ${client?.name || 'Ons Team'}`}
                                       </h1>
                                       <p className="text-white/90 font-medium text-lg max-w-lg">
                                          {customBranding?.heroSubtitle || 'Ontdek onze openstaande vacatures en solliciteer direct op een nieuwe uitdaging wegens uitbreiding.'}
                                       </p>
                                       <div className="flex flex-wrap gap-4 pt-4">
                                          <button onClick={() => scrollToSection('vacancies')} className="text-white px-8 py-4 rounded-sm font-black text-sm uppercase tracking-widest transition-transform hover:-translate-y-1 shadow-lg" style={{ backgroundColor: customBranding?.primaryColor || '#e51b23' }}>
                                             Bekijk Vacatures
                                          </button>
                                       </div>
                                    </div>
                                 </div>
                              </section>
                              {renderVacancies({ id: 'vacancies', type: 'vacancies', title: 'Vacatures', isVisible: true })}
                           </>
                        );
                     }
                  })()}
               </main>
            ) : (
               /* DETAIL VIEW + APPLICATION WIZARD (Identical to before, but RICH TEXT support added) */
               <main className={`animate-in slide-in-from-bottom-4 duration-500 bg-white ${isEmbedded ? '' : ''}`}>
                  {(() => {
                    const displayedClient = selectedVacancy.eindklant || selectedVacancy.client || selectedVacancy.clients;
                    const opdrachtNaam = selectedVacancy.data?.clientName || displayedClient?.name || client?.name || 'Werkgever';
                    const vLoc = selectedVacancy.data?.location || client.address_city || 'Nederland';
                    
                    const ldJson = {
                      "@context": "https://schema.org/",
                      "@type": "JobPosting",
                      "title": selectedVacancy.title,
                      "description": selectedVacancy.data?.finalVacancyContent || selectedVacancy.description || selectedVacancy.title,
                      "identifier": {
                         "@type": "PropertyValue",
                         "name": opdrachtNaam,
                         "value": selectedVacancy.id || 'vacature'
                      },
                      "datePosted": selectedVacancy.createdAt || selectedVacancy.created_at || new Date().toISOString(),
                      "validThrough": selectedVacancy.deadline || selectedVacancy.data?.deadline ? new Date(selectedVacancy.deadline || selectedVacancy.data?.deadline).toISOString() : new Date(Date.now() + 90*24*60*60*1000).toISOString(),
                      "employmentType": (selectedVacancy.data?.commitment || '').toLowerCase().includes('part') ? "PART_TIME" : "FULL_TIME",
                      "hiringOrganization": {
                        "@type": "Organization",
                        "name": opdrachtNaam,
                        "logo": selectedVacancy.data?.clientLogo || displayedClient?.logo_url || client?.logo_url
                      },
                      "jobLocation": {
                        "@type": "Place",
                        "address": {
                          "@type": "PostalAddress",
                          "addressLocality": vLoc,
                          "addressCountry": "NL"
                        }
                      }
                    };
                    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />;
                  })()}
                  {!isEmbedded && (
                     <div className="py-20 px-6" style={{ backgroundColor: customBranding?.headerColor || 'rgb(229, 27, 35)', color: customBranding?.headerTextColor || '#FFFFFF' }}>
                        <div className="max-w-7xl mx-auto">
                           <button onClick={() => setView('home')} className="flex items-center gap-2 text-xs font-bold opacity-60 hover:opacity-100 mb-8 uppercase tracking-widest transition-opacity"><ArrowLeft size={14} /> Terug naar overzicht</button>
                           <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
                              <div>
                                 <span className="inline-block px-3 py-1 bg-white/10 rounded-md text-[10px] font-black uppercase tracking-widest mb-4 backdrop-blur-sm border border-white/20">
                                    {selectedVacancy.data?.category || 'Vacature'}
                                 </span>
                                 <h1 className="text-4xl md:text-6xl font-black uppercase italic tracking-tight mb-2 leading-none">{selectedVacancy.title}</h1>
                                 <div className="flex items-center gap-6 mt-6 opacity-80 text-sm font-medium flex-wrap">
                                    <span className="flex items-center gap-2"><MapPin size={16} /> {selectedVacancy.data?.location || client.address_city || 'Remote'}</span>
                                    <span className="flex items-center gap-2"><Clock size={16} /> Fulltime</span>
                                    <span className="flex items-center gap-2"><User size={16} /> {selectedVacancy.data?.experience || 'Medior/Senior'}</span>
                                    {(selectedVacancy.createdAt || selectedVacancy.created_at) && (
                                       <span className="flex items-center gap-2"><Calendar size={16} /> Aangemaakt op {selectedVacancy.createdAt || new Date(selectedVacancy.created_at).toLocaleDateString('nl-NL')}</span>
                                    )}
                                 </div>
                              </div>
                              <button onClick={() => setShowWizard(true)} className="px-8 py-4 bg-white text-slate-900 rounded-sm font-black text-sm uppercase tracking-widest hover:bg-slate-100 shadow-xl transition-transform hover:-translate-y-1">
                                 Direct Solliciteren
                              </button>
                           </div>
                        </div>
                     </div>
                  )}

                  <div className={`max-w-7xl mx-auto px-6 ${isEmbedded ? 'py-6' : 'py-16'} grid grid-cols-1 lg:grid-cols-12 gap-16`}>
                     <div className={`${isEmbedded ? 'col-span-12' : 'lg:col-span-8'} space-y-12`}>
                        {isEmbedded && (
                           <div className="mb-8">
                              <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 rounded-md text-[10px] font-black uppercase tracking-widest mb-4">
                                 {selectedVacancy.data?.category || 'Vacature'}
                              </span>
                              <h1 className="text-3xl font-black uppercase italic tracking-tight mb-4 text-slate-900 leading-none">{selectedVacancy.title}</h1>
                              <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-slate-500 uppercase tracking-wide">
                                 <span className="flex items-center gap-1.5"><MapPin size={14} className="text-slate-400" /> {selectedVacancy.data?.location || client.address_city || 'Remote'}</span>
                                 <span className="flex items-center gap-1.5"><Clock size={14} className="text-slate-400" /> Fulltime</span>
                              </div>
                           </div>
                        )}
                        {/* Description */}
                        <div className="prose prose-lg prose-slate max-w-none">
                           {!selectedVacancy.data?.finalVacancyContent && (
                              <h3 className="uppercase italic font-black text-2xl mb-4 text-slate-900">Over de rol</h3>
                           )}
                           {selectedVacancy.data?.finalVacancyContent ? (
  <div 
    style={{ 
       '--v-font': selectedVacancy.data?.vacancySettings?.fontFamily || 'inherit',
       '--v-color': selectedVacancy.data?.vacancySettings?.primaryColor || '#0f172a',
       '--v-h-size': selectedVacancy.data?.vacancySettings?.headingSize === 'lg' ? '2.5rem' : selectedVacancy.data?.vacancySettings?.headingSize === 'sm' ? '1.25rem' : '1.75rem',
       '--v-t-size': selectedVacancy.data?.vacancySettings?.textSize === 'lg' ? '1.125rem' : selectedVacancy.data?.vacancySettings?.textSize === 'sm' ? '0.875rem' : '1rem',
       fontFamily: 'var(--v-font)',
       fontSize: 'var(--v-t-size)'
    } as any}
    className={"leading-relaxed text-slate-600 [&>h3]:uppercase [&>h3]:italic [&>h3]:font-black [&>h3]:text-[length:var(--v-h-size)] [&>h3]:mb-6 [&>h3]:text-[color:var(--v-color)] [&>h3]:border-l-4 [&>h3]:border-[color:var(--v-color)] [&>h3]:pl-4 [&>h3]:mt-10 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-4 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mb-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-4 [&>li]:mb-1 [&>p]:mb-4 [&>strong]:font-bold [&>b]:font-bold"}
    dangerouslySetInnerHTML={{ __html: selectedVacancy.data.finalVacancyContent }} 
  />
                           ) : (
                              <div className="leading-relaxed text-slate-600 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-4 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mb-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-4 [&>li]:mb-1 [&>p]:mb-4 [&>strong]:font-bold [&>b]:font-bold" dangerouslySetInnerHTML={{ __html: marked.parse(selectedVacancy.description || '') as string }} />
                           )}
                        </div>

                        {!selectedVacancy.data?.finalVacancyContent && (
                           <>

                        {/* Responsibilities */}
                        {selectedVacancy.responsibilities && (
                           <div>
                              <h3 className="uppercase italic font-black text-2xl mb-6 text-slate-900 border-l-4 border-slate-900 pl-4">Wat je gaat doen</h3>
                              {typeof selectedVacancy.responsibilities === 'string' ? (
                                 <div className="leading-relaxed text-slate-600 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-4 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mb-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-4 [&>li]:mb-1 [&>p]:mb-4" dangerouslySetInnerHTML={{ __html: marked.parse(selectedVacancy.responsibilities || '') as string }} />
                              ) : (
                                 <ul className="space-y-4">
                                    {Object.entries(selectedVacancy.responsibilities).map(([key, val]: any) => (
                                       <li key={key} className="flex items-start gap-4">
                                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5 text-slate-900"><CheckCircle2 size={14} /></div>
                                          <span className="text-slate-700 font-medium leading-relaxed">{val}</span>
                                       </li>
                                    ))}
                                 </ul>
                              )}
                           </div>
                        )}

                        {/* Requirements */}
                        {selectedVacancy.requirements && (
                           <div>
                              <h3 className="uppercase italic font-black text-2xl mb-6 text-slate-900 border-l-4 border-slate-900 pl-4">Wat wij vragen</h3>
                              {typeof selectedVacancy.requirements === 'string' ? (
                                 <div className="leading-relaxed text-slate-600 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-4 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mb-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-4 [&>li]:mb-1 [&>p]:mb-4" dangerouslySetInnerHTML={{ __html: marked.parse(selectedVacancy.requirements || '') as string }} />
                              ) : (
                                 <ul className="space-y-4">
                                    {Array.isArray(selectedVacancy.requirements) ? selectedVacancy.requirements.map((req: string, i: number) => (
                                       <li key={i} className="flex items-start gap-4">
                                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5 text-slate-900"><CheckCircle2 size={14} /></div>
                                          <span className="text-slate-700 font-medium leading-relaxed">{req}</span>
                                       </li>
                                    )) : null}
                                 </ul>
                              )}
                           </div>
                        )}

                        {/* Benefits */}
                        {selectedVacancy.benefits && (
                           <div>
                              <h3 className="uppercase italic font-black text-2xl mb-6 text-slate-900 border-l-4 border-slate-900 pl-4">Wat wij bieden</h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 {Array.isArray(selectedVacancy.benefits) ? selectedVacancy.benefits.map((ben: string, i: number) => (
                                    <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                                       <Zap size={16} className="text-yellow-500 shrink-0" />
                                       <span className="text-sm font-bold text-slate-700">{ben}</span>
                                    </div>
                                 )) : (
                                    <div className="leading-relaxed text-slate-600 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-4 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mb-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-4 [&>li]:mb-1 [&>p]:mb-4 md:col-span-2" dangerouslySetInnerHTML={{ __html: marked.parse(selectedVacancy.benefits || '') as string }} />
                                 )}
                              </div>
                           </div>
                        )}

                        {/* AI Match Box */}
                        <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white shadow-xl relative overflow-hidden">
                           <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20"></div>
                           <h4 className="font-bold mb-4 flex items-center gap-2 text-lg"><Sparkles size={18} className="text-yellow-400" /> Waarom deze rol bij jou past:</h4>
                           <p className="text-slate-300 leading-relaxed relative z-10">
                              "Deze rol is perfect voor iemand die houdt van {selectedVacancy.data?.tags?.[0] || 'uitdagingen'} en {selectedVacancy.data?.tags?.[1] || 'innovatie'}.
                              Bij {client.name} krijg je de vrijheid om impact te maken."
                           </p>
                        </div>
                           </>
                        )}
                     </div>

                     <div className="lg:col-span-4 space-y-8">
                        {!isEmbedded && (
                           <>
                              {(() => {
                                 const displayedClient = selectedVacancy.eindklant || selectedVacancy.client || selectedVacancy.clients;
                                 const logoUrl = selectedVacancy.data?.clientLogo || displayedClient?.logo_url || client?.logo_url;
                                 if (logoUrl) {
                                    return (
                                       <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex justify-center items-center">
                                          <img src={logoUrl} alt={selectedVacancy.data?.clientName || displayedClient?.name || client?.name || 'Logo'} className="max-h-16 object-contain" />
                                       </div>
                                    );
                                 }
                                 return null;
                              })()}
                              <div className="bg-slate-50 p-8 rounded-xl border border-slate-100 sticky top-24">
                                 <h3 className="font-black text-lg text-slate-900 uppercase italic mb-6">Solliciteer Nu</h3>
                                 <p className="text-xs text-slate-500 font-medium mb-6">Upload je CV en laat onze AI een perfecte match analyse maken.</p>

                                 <div className="space-y-3">
                                    <button onClick={() => setShowWizard(true)} className="w-full text-white py-4 rounded-sm font-black text-sm uppercase tracking-widest hover:brightness-110 shadow-lg flex items-center justify-center gap-2 transition-all hover:scale-[1.02]" style={{ backgroundColor: customBranding?.vacancyButtonColor || customBranding?.primaryColor || '#e51b23' }}>
                                       Start Sollicitatie
                                    </button>
                                    <button 
                                       onClick={() => setShowLinkedInModal(true)}
                                       className="w-full text-white py-3 rounded-sm font-bold text-xs uppercase tracking-widest hover:brightness-110 flex items-center justify-center gap-2 shadow-md transition-all"
                                       style={{ backgroundColor: customBranding?.linkedinButtonColor || '#0077b5' }}
                                    >
                                       <Linkedin size={16} /> Blijf op de hoogte
                                    </button>
                                    <p className="text-[10px] text-center text-slate-400 font-medium">Deel je profiel en wij houden je up-to-date.</p>
                                 </div>
                              </div>

                              {client.contact_person && (
                                 <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm mt-8">
                                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">Contactpersoon</h4>
                                    <div className="flex items-center gap-4">
                                       <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 overflow-hidden">
                                          <UserCircle2 size={32} />
                                       </div>
                                       <div>
                                          <p className="font-bold text-slate-900">{client.contact_person}</p>
                                          <p className="text-xs text-slate-500">Recruiter</p>
                                       </div>
                                    </div>
                                    <button className="w-full mt-4 py-3 border border-slate-200 rounded text-xs font-bold text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-2">
                                       <Mail size={14} /> Stuur bericht
                                    </button>
                                 </div>
                              )}
                           </>
                        )}
                     </div>
                  </div>
               </main>
            )}
         </div>

         {showWizard && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/90 backdrop-blur-md animate-in fade-in duration-300">
               <div className="bg-white w-full max-w-2xl h-[90vh] sm:h-auto rounded-xl shadow-2xl overflow-hidden flex flex-col">
                  <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                     <div>
                        <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                           Solliciteren
                        </h2>
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{client?.name || 'Verstuur je details'}</p>
                     </div>
                     <button onClick={() => setShowWizard(false)} className="p-2 hover:bg-slate-200 rounded-full transition-colors"><X size={20} /></button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-8 custom-scrollbar relative">
                     {/* Step Indicator */}
                     <div className="flex items-center justify-between mb-8 px-2 relative">
                        {[1, 2, 3].map(step => (
                           <div key={step} className="flex flex-col items-center relative z-10 w-1/3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500 ${step <= wizardStep ? 'text-white scale-110 shadow-md' : 'bg-slate-100 text-slate-400'}`} style={{ backgroundColor: step <= wizardStep ? (customBranding?.primaryColor || '#e51b23') : undefined }}>
                                 {step < wizardStep ? <CheckCircle size={16} /> : step}
                              </div>
                              <span className={`text-[9px] font-bold uppercase tracking-widest mt-2 ${step <= wizardStep ? 'text-slate-800' : 'text-slate-300'}`}>
                                 {['Gegevens', 'CV & Voorwaarden', 'Klaar'][step - 1]}
                              </span>
                           </div>
                        ))}
                        <div className="absolute top-4 left-[16.6%] right-[16.6%] w-2/3 h-0.5 bg-slate-100 -z-0"></div>
                        <div className="absolute top-4 left-[16.6%] h-0.5 transition-all duration-500 -z-0" style={{ width: `${((wizardStep - 1) / 2) * 66}%`, backgroundColor: customBranding?.primaryColor || '#e51b23' }}></div>
                     </div>

                     {/* STEP 1: PERSONAL DETAILS */}
                     {wizardStep === 1 && (
                        <div className="space-y-6 animate-in slide-in-from-right-4">
                           <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                              <h3 className="font-bold text-slate-900">Jouw Gegevens</h3>
                              <p className="text-xs text-slate-500 mt-1">Vul je contactgegevens in zodat we je snel kunnen bereiken.</p>
                           </div>
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-1">
                                 <label className="text-[10px] font-bold uppercase text-slate-500">Voornaam *</label>
                                 <input type="text" value={applicant.firstName} onChange={e => setApplicant({ ...applicant, firstName: e.target.value })} className="w-full p-2 border rounded text-sm font-bold shadow-sm" required />
                              </div>
                              <div className="space-y-1">
                                 <label className="text-[10px] font-bold uppercase text-slate-500">Achternaam</label>
                                 <input type="text" value={applicant.lastName} onChange={e => setApplicant({ ...applicant, lastName: e.target.value })} className="w-full p-2 border rounded text-sm font-bold shadow-sm" />
                              </div>
                              <div className="space-y-1">
                                 <label className="text-[10px] font-bold uppercase text-slate-500">Email *</label>
                                 <input type="email" value={applicant.email} onChange={e => setApplicant({ ...applicant, email: e.target.value })} className="w-full p-2 border rounded text-sm font-bold shadow-sm" required />
                              </div>
                              <div className="space-y-1">
                                 <label className="text-[10px] font-bold uppercase text-slate-500">Telefoonnummer</label>
                                 <input type="tel" placeholder="+31 6 12345678" value={applicant.phone || ''} onChange={e => setApplicant({ ...applicant, phone: e.target.value })} className="w-full p-2 border rounded text-sm font-bold shadow-sm" />
                              </div>
                              <div className="space-y-1">
                                 <label className="text-[10px] font-bold uppercase text-slate-500">LinkedIn URL</label>
                                 <input type="url" placeholder="https://linkedin.com/in/..." value={applicant.linkedin || ''} onChange={e => setApplicant({ ...applicant, linkedin: e.target.value })} className="w-full p-2 border rounded text-sm font-bold shadow-sm" />
                              </div>
                              <div className="space-y-1">
                                 <label className="text-[10px] font-bold uppercase text-slate-500">Land</label>
                                 <input type="text" placeholder="Nederland" value={applicant.country || ''} onChange={e => setApplicant({ ...applicant, country: e.target.value })} className="w-full p-2 border rounded text-sm font-bold shadow-sm" />
                              </div>
                              <div className="space-y-1 md:col-span-2">
                                 <label className="text-[10px] font-bold uppercase text-slate-500">Huidige Status</label>
                                 <select value={applicant.currentStatus || ''} onChange={e => setApplicant({ ...applicant, currentStatus: e.target.value })} className="w-full p-2 border rounded text-sm font-bold shadow-sm bg-white">
                                    <option value="">Selecteer status...</option>
                                    <option value="Open to work">Open to work (Beschikbaar)</option>
                                    <option value="Employed">In loondienst (Niet actief op zoek)</option>
                                    <option value="Freelance">Freelance / ZZP</option>
                                    <option value="Student">Student / Starter</option>
                                 </select>
                              </div>
                           </div>
                        </div>
                     )}

                     {/* STEP 2: CV UPLOAD & CONSENT */}
                     {wizardStep === 2 && (
                        <div className="flex flex-col space-y-8 animate-in slide-in-from-right-4">
                           <div className="flex flex-col items-center justify-center p-8 bg-slate-50 border border-slate-200 rounded-xl">
                              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center border-2 border-dashed border-slate-300 relative group cursor-pointer hover:border-indigo-400 hover:bg-slate-50 transition-all mb-4" style={{ borderColor: file ? (customBranding?.primaryColor || '#e51b23') : undefined }}>
                                 <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                                 <Upload size={28} className="text-slate-400 group-hover:text-slate-700 transition-colors" />
                              </div>
                              
                              <div className="text-center">
                                 <h3 className="font-bold text-slate-900">{file ? `Geselecteerd: ${file.name}` : 'Document Uploaden'}</h3>
                                 {isAnalyzing && (
                                    <div className="flex flex-col items-center justify-center mt-3">
                                       <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-widest animate-pulse">
                                          <Loader2 className="animate-spin" size={14} /> Controle wordt uitgevoerd...
                                       </div>
                                    </div>
                                 )}
                                 {analysisDone && !isAnalyzing && file && (
                                    <div className="mt-2 flex items-center justify-center gap-2 text-emerald-600 font-bold text-[10px] uppercase tracking-widest animate-in fade-in">
                                       <CheckCircle2 size={14} /> CV is succesvol verwerkt
                                    </div>
                                 )}
                                 {file && !isAnalyzing && <button onClick={() => { setFile(null); setAnalysisDone(false); setParsedData(null); }} className="text-red-500 text-xs font-bold mt-3 hover:underline">Bestand wijzigen</button>}
                                 {!file && <p className="text-xs text-slate-400 mt-1">Accepteert PDF of Word bestanden</p>}
                              </div>
                           </div>

                           <div className="space-y-4 pt-4 border-t border-slate-100">
                              <label className="flex items-start gap-3 cursor-pointer group">
                                 <div className="relative flex items-center mt-0.5">
                                    <input type="checkbox" checked={applicant.consentPrivacy} onChange={e => setApplicant({ ...applicant, consentPrivacy: e.target.checked })} className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 shadow-sm transition-all focus:ring-2 focus:ring-offset-1 focus:outline-none" style={{ backgroundColor: applicant.consentPrivacy ? (customBranding?.primaryColor || '#e51b23') : undefined, borderColor: applicant.consentPrivacy ? (customBranding?.primaryColor || '#e51b23') : undefined }} />
                                    <CheckCircle2 size={12} className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 transition-opacity peer-checked:opacity-100" />
                                 </div>
                                 <span className="text-xs text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">
                                    {customBranding?.consentTexts?.privacyPolicy || 'Door deze sollicitatie te versturen, geef je ons toestemming om je gegevens op te slaan voor de beoordeling van je geschiktheid voor de functie.'}
                                 </span>
                              </label>

                              <label className="flex items-start gap-3 cursor-pointer group">
                                 <div className="relative flex items-center mt-0.5">
                                    <input type="checkbox" checked={applicant.consentTerms} onChange={e => setApplicant({ ...applicant, consentTerms: e.target.checked })} className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 shadow-sm transition-all focus:ring-2 focus:ring-offset-1 focus:outline-none" style={{ backgroundColor: applicant.consentTerms ? (customBranding?.primaryColor || '#e51b23') : undefined, borderColor: applicant.consentTerms ? (customBranding?.primaryColor || '#e51b23') : undefined }} />
                                    <CheckCircle2 size={12} className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 transition-opacity peer-checked:opacity-100" />
                                 </div>
                                 <span className="text-xs text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">
                                    {customBranding?.consentTexts?.termsOfService || 'Ik ga akkoord met de algemene voorwaarden van dit recruitment traject.'}
                                 </span>
                              </label>
                           </div>
                        </div>
                     )}

                     {/* STEP 3: KLAAR */}
                     {wizardStep === 3 && (
                        <div className="flex flex-col items-center justify-center py-8 animate-in zoom-in duration-300 text-center">
                           <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6 shadow-md" style={{ backgroundColor: `${customBranding?.primaryColor || '#e51b23'}15`, color: customBranding?.primaryColor || '#e51b23' }}>
                              <CheckCircle size={40} />
                           </div>
                           <h3 className="text-2xl font-black uppercase italic tracking-tight text-slate-900 mb-2">Sollicitatie Ontvangen!</h3>
                           <p className="text-slate-500 mb-8 max-w-sm">Dankjewel voor je interesse. Je profiel staat stevig bij ons in het systeem en we pakken het zo soepel mogelijk verder op.</p>

                           <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 w-full max-w-sm text-left">
                              <h4 className="font-bold text-xs uppercase tracking-widest text-slate-400 mb-4">Wat gebeurt er nu?</h4>
                              <ul className="space-y-4">
                                 <li className="flex gap-3">
                                    <div className="w-5 h-5 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold text-slate-400">1</div>
                                    <div className="text-sm">
                                       <span className="font-bold text-slate-700 block line-through opacity-50">Profiel Ingestuurd</span>
                                    </div>
                                 </li>
                                 <li className="flex gap-3 relative">
                                    <div className="absolute left-[9px] -top-3 bottom-0 w-0.5 bg-slate-200 -z-10"></div>
                                    <div className="w-5 h-5 rounded-full text-white flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold ring-4 ring-slate-50 relative z-10" style={{ backgroundColor: customBranding?.primaryColor || '#e51b23' }}>2</div>
                                    <div className="text-sm">
                                       <span className="font-bold text-slate-900 block">Kwalitatieve Beoordeling</span>
                                       <span className="text-slate-500 text-xs">Onze specialisten bekijken je input en checken de aansluiting.</span>
                                    </div>
                                 </li>
                                 <li className="flex gap-3">
                                    <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold text-slate-400">3</div>
                                    <div className="text-sm">
                                       <span className="font-bold text-slate-700 block">Persoonlijk Contact</span>
                                       <span className="text-slate-500 text-xs">Bij een match of vragen plannen we een oriënterend gesprek.</span>
                                    </div>
                                 </li>
                              </ul>
                           </div>

                           <button onClick={() => { setShowWizard(false); setWizardStep(1); }} className="mt-8 px-8 py-3 bg-white border border-slate-200 text-slate-700 rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-slate-50 transition-colors shadow-sm">
                              Terug naar overzicht
                           </button>
                        </div>
                     )}
                  </div>

                  {wizardStep < 3 && (
                     <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center mt-auto shrink-0">
                        {wizardStep > 1 ? (
                           <button onClick={() => setWizardStep(wizardStep - 1)} className="px-6 py-3 rounded-lg font-bold text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors">
                              Terug
                           </button>
                        ) : <div />}

                        {wizardStep === 1 ? (
                           <button 
                              onClick={() => setWizardStep(2)} 
                              disabled={(!applicant.email || !applicant.firstName)}
                              className="px-8 py-3 text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:brightness-110 transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
                              style={{ backgroundColor: customBranding?.primaryColor || '#e51b23' }}
                           >
                              Volgende <ArrowRight size={14} />
                           </button>
                        ) : (
                           <button 
                              onClick={handleSubmitApplication} 
                              disabled={isSubmitting || !applicant.consentPrivacy || !applicant.consentTerms}
                              className="px-8 py-3 text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:brightness-110 transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
                              style={{ backgroundColor: customBranding?.primaryColor || '#e51b23' }}
                           >
                              {isSubmitting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Verstuur Sollicitatie
                           </button>
                        )}
                     </div>
                  )}
               </div>
            </div>
         )}
         {showLinkedInModal && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6 bg-slate-900/90 backdrop-blur-md animate-in fade-in duration-300">
               <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden flex flex-col">
                  <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-[#0077b5] text-white">
                     <div>
                        <h2 className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
                           <Linkedin size={20} /> LinkedIn Connectie
                        </h2>
                        <p className="text-xs font-bold uppercase tracking-widest opacity-80">Blijf op de hoogte bij {client?.name}</p>
                     </div>
                     <button onClick={() => setShowLinkedInModal(false)} className="p-2 hover:bg-white/20 rounded-full transition-colors"><X size={20} /></button>
                  </div>
                  
                  <div className="p-8">
                     <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                        Laat je gegevens achter inclusief je LinkedIn profiel. Wij nemen contact met je op zodra er perfecte kansen matchen met jouw profiel!
                     </p>
                     
                     <form onSubmit={handleSubmitLinkedIn} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                           <div>
                              <label className="block text-xs font-bold text-slate-700 mb-1">Voornaam *</label>
                              <input required type="text" value={applicant.firstName} onChange={e => setApplicant({...applicant, firstName: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-[#0077b5]" />
                           </div>
                           <div>
                              <label className="block text-xs font-bold text-slate-700 mb-1">Achternaam *</label>
                              <input required type="text" value={applicant.lastName} onChange={e => setApplicant({...applicant, lastName: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-[#0077b5]" />
                           </div>
                        </div>
                        <div>
                           <label className="block text-xs font-bold text-slate-700 mb-1">E-mailadres *</label>
                           <input required type="email" value={applicant.email} onChange={e => setApplicant({...applicant, email: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-[#0077b5]" />
                        </div>
                        <div>
                           <label className="block text-xs font-bold text-slate-700 mb-1">LinkedIn URL *</label>
                           <input required type="url" placeholder="https://linkedin.com/in/jouwprofiel" value={applicant.linkedin} onChange={e => setApplicant({...applicant, linkedin: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-[#0077b5]" />
                        </div>
                        
                        <button type="submit" disabled={isSubmitting} className="w-full mt-6 bg-[#0077b5] text-white py-4 rounded-lg font-black text-sm uppercase tracking-widest hover:brightness-110 shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50">
                           {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Verbinden
                        </button>
                     </form>
                  </div>
               </div>
            </div>
         )}
         {!isEmbedded && <Footer />}
      </div>
   );
};

export default PublicVacancyView;
